import { useEffect, useState } from "react";
import { useApi } from "@/context/ApiContext";
import { ensureProviderInfos, findProviderInfo, type ProviderInfos } from "@/lib/provider-infos";

/* Lettered tile from the design (`.xmark`): shown until the server's logo loads, or when it has none. */
const LETTER_MARKS: Record<string, { letter: string; bg: string; fg: string }> = {
  binance: { letter: "B", bg: "#F3BA2F", fg: "#111" },
  mexc: { letter: "M", bg: "#1E6FFF", fg: "#fff" },
  revolutx: { letter: "R", bg: "#E8EAED", fg: "#111" },
  bybit: { letter: "By", bg: "#F7A600", fg: "#111" },
  okx: { letter: "OK", bg: "#F5F5F5", fg: "#111" },
  kraken: { letter: "K", bg: "#7132F5", fg: "#fff" },
  gateio: { letter: "G", bg: "#2354E6", fg: "#fff" },
};

/** Shared across marks so a list of rows renders its logos in one pass. */
let loaded: ProviderInfos | null = null;

/**
 * Exchange mark. Uses the icon or logo the server sends with
 * `configurable.providers` (data URIs), on a light tile so dark logos stay
 * readable in both themes. `size` is the tile height in px.
 */
export function ExchangeMark({ provider, size = 28 }: { provider?: string; size?: number }) {
  const api = useApi([]);
  const [infos, setInfos] = useState<ProviderInfos | null>(loaded);

  useEffect(() => {
    if (infos) return;
    let cancelled = false;
    ensureProviderInfos(api.commands)
      .then((i) => {
        loaded = i;
        if (!cancelled) setInfos(i);
      })
      .catch(() => {
        // Older servers without configurable.providers: keep the lettered tile.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.ready]);

  const key = String(provider || "").toLowerCase();
  const info = findProviderInfo(infos, key);
  const radius = Math.max(4, Math.round(size * 0.28));

  if (info?.icon) {
    return (
      <span
        aria-hidden
        className="grid shrink-0 place-items-center border border-[var(--border)] bg-white"
        style={{ width: size, height: size, borderRadius: radius }}
      >
        <img src={info.icon} alt="" style={{ width: size * 0.64, height: size * 0.64 }} className="object-contain" />
      </span>
    );
  }

  if (info?.logo) {
    // Wordmark: fixed height, width follows the logo.
    const pad = Math.max(3, Math.round(size * 0.2));
    return (
      <span
        aria-hidden
        className="inline-flex shrink-0 items-center border border-[var(--border)] bg-white"
        style={{ height: size, padding: `0 ${pad + 1}px`, borderRadius: radius }}
      >
        <img
          src={info.logo}
          alt=""
          style={{ height: Math.round(size * 0.5), maxWidth: size * 3.2 }}
          className="w-auto object-contain"
        />
      </span>
    );
  }

  const m = LETTER_MARKS[key] || { letter: (key[0] || "?").toUpperCase(), bg: "var(--bg-hover)", fg: "var(--text-primary)" };
  return (
    <span
      aria-hidden
      className="grid shrink-0 place-items-center font-bold"
      style={{ width: size, height: size, background: m.bg, color: m.fg, fontSize: Math.round(size * 0.39), borderRadius: radius }}
    >
      {m.letter}
    </span>
  );
}
